<template>
  <Modal circleClose widthFitContent position="center" @update:show="handleShow">
    <template v-if="isMobile === true">
      <div class="container mobile" :style="{ 'aspect-ratio': aspectRatio }">
        <VideoItem autoplay :controls="false" controlslist="nodownload nofullscreen noremoteplayback"
          :poster="item?.coverUrl" x5-video-player-type="h5" webkit-playsinline="true" x-webkit-airplay="true"
          playsinline="true" x5-playsinline preload="metadata" class="video" :src="item?.videoUrl" ref="videoRef" />
      </div>
    </template>
    <template v-else>
      <div class="container">
        <div class="info">
          <img class="avatar" v-if="item?.iconUrl" :src="item?.iconUrl" />
          <div class="name" v-if="item?.ksNickName">{{ item?.ksNickName }}</div>
        </div>
        <GCVideo autoplay controls
          :controlslist="`nodownload ${allowPCFullScreen ? '' : 'nofullscreen'} noremoteplayback`"
          :poster="item?.coverUrl" x5-video-player-type="h5" webkit-playsinline="true" x-webkit-airplay="true"
          playsinline="true" x5-playsinline disablepictureinpicture disableremoteplayback preload="metadata"
          class="video" :src="item?.videoUrl" ref="videoRef" :class="`${allowPCFullScreen ? 'fullscreen' : ''}`" />
        <div v-if="isLargeScreen === true" class="op">
          <van-button @click="currentIndex--" round class="up" icon="arrow-up" :disabled="currentIndex === 0" />
          <van-button round @click="handleNext" class="down" icon="arrow-down" :disabled="isDownDisabled" />
        </div>
      </div>
    </template>
  </Modal>
</template>
<script setup lang="ts">
import Modal from "../common/modal.vue";
import { Button as VanButton } from "vant";
import { PublishListItem } from "../common/api/request";
import useDevice from "../common/useDevice";
import GCVideo from "../common/GCVideo.vue";
import VideoItem from "../common/VideoItem.vue";
const props = withDefaults(
  defineProps<{
    index: number;
    items: PublishListItem[];
    allowPCFullScreen?: boolean;
  }>(),
  { items: () => [], index: 0, allowPCFullScreen: false },
);

type Event = {
  (e: "next"): void;
  (e: "update:show", value: boolean): void;
};
const emit = defineEmits<Event>();
const { isSafari, isMobile } = useDevice();
const videoRef = ref<{ videoRef: HTMLVideoElement }>();
const isLargeScreen = ref(false);
const resize = () => {
  isLargeScreen.value = window.innerWidth >= 1360;
};
onMounted(() => {
  resize();
});
window.addEventListener("resize", resize);
window.addEventListener("orientationchange", resize);
onBeforeUnmount(() => {
  window.removeEventListener("resize", resize);
  window.removeEventListener("orientationchange", resize);
})

const currentItems = ref(props?.items ?? []);
const currentIndex = ref(props?.index ?? 0);
const isDownDisabled = computed(
  () => currentIndex.value === currentItems.value?.length - 1,
);

const handleShow = (visible: boolean) => {
  emit('update:show', visible);
  if (visible) {
    videoRef.value?.videoRef?.play?.();
  } else {
    videoRef.value?.videoRef?.pause?.();
  }
};

watch(
  () => props.items,
  (value) => {
    currentItems.value = value;
  },
);

watch(
  () => props.index,
  (value) => (currentIndex.value = value),
);

const item = computed(() => currentItems.value[currentIndex.value] ?? {});

const aspectRatio = computed(() => {
  if (item.value?.vedioWeight < item.value?.vedioHeight) {
    return `${item.value?.vedioWeight}/${item.value?.vedioHeight}`
  }
});

const handleNext = () => {
  currentIndex.value++;
  if (currentIndex.value === currentItems.value.length - 1) {
    emit("next");
  }
};

</script>
<style scoped lang="less">
.container {
  width: 27.2rem;
  aspect-ratio: 1360 / 765;
  max-height: calc(100vh - 1rem);
  min-height: 1.92rem;
  position: relative;
  overflow: hidden;;

  @media screen and (max-width: 1360px) {
    width: 5.6rem;
    border-radius: 0.24rem;
    aspect-ratio: 283 / 497;
  }

  .video {
    width: 100%;
    height: 100%;
  }

  &.mobile {
    .info {
      padding-top: 1rem;

      @media screen and (max-width:1360px) {
        top: 0;
        padding-top: 0.32rem;
        background: #000;
      }
    }

    @media screen and (max-width:1360px) {
      overflow: hidden;
      padding-top: 0;
    }
  }

  .info {
    position: absolute;
    left: 0;
    top: 0px;
    width: 100%;
    display: flex;
    align-items: center;
    padding: 0.48rem;
    pointer-events: none;

    @media screen and (max-width: 1360px) {
      padding: 0.32rem;
      // top: 20px;
    }

    .avatar {
      width: 0.48rem;
      height: 0.48rem;
      border-radius: 50%;
      display: inline-block;
      margin-right: 0.16rem;

      @media screen and (max-width: 1360px) {
        width: 0.32rem;
        height: 0.32rem;
      }
    }

    .name {
      font-size: 0.32rem;
      line-height: 0.48rem;
      font-weight: 400;
      color: #fff;

      @media screen and (max-width: 1360px) {
        font-size: 0.2rem;
        line-height: 0.28rem;
        color: rgba(255, 255, 255, 0.7);
      }
    }
  }

  .op {
    position: absolute;
    top: 50%;
    right: 0.48rem;
    margin: auto 0;
    display: flex;
    flex-direction: column;

    .up,
    .down {
      width: 0.96rem;
      height: 0.96rem;
      border-radius: 50%;
      display: flex;
      justify-content: center;
      align-items: center;
      color: rgba(215, 215, 215, 1);
      background-color: rgba(34, 34, 34, 0.4);
      border: 0.0162rem solid rgba(255, 255, 255, 0.08);
    }

    .up {
      margin-bottom: 0.32rem;
    }
  }
}
</style>

<style lang="less">
.popup-container.van-popup.circleClose.mobile {
  border-bottom-left-radius: .32rem;
  border-bottom-right-radius: .32rem;
  padding-bottom: 0rem;
  border-color: rgba(255, 255, 255, 0.12);
  border-width: 1px;
  border-style: solid;
  background-color: #000;

  @media screen and (max-width: 1360px) {
    // border: none;
  }

  .van-popup {
    &__close-icon {
      top: .24rem;
      right: 0.24rem;
    }
  }
}

video {
  &::-webkit-media-controls-fullscreen-button {
    display: none;
  }

  &.fullscreen {
    &::-webkit-media-controls-fullscreen-button {
      display: block;
    }
  }
}
</style>
